import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Loader2 } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { FadeUp } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL = "support@anchorsideglobal.com";

const NEEDS = [
  { value: "General Admin", label: "General Admin" },
  { value: "Full Paraplanning", label: "Full Paraplanning" },
  { value: "Both", label: "Both" },
  { value: "Other", label: "Other" },
];

const inputClass =
  "w-full bg-transparent border-b border-petrol/25 py-3.5 font-sans text-petrol placeholder:text-petrol/40 focus:border-verdigris transition-colors";

export const Contact = () => {
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    firm_name: "",
    primary_need: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.firm_name || !form.primary_need) {
      toast.error("Please complete all required fields.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, form);
      toast.success("Inquiry received. We'll be in touch shortly.");
      setForm({ full_name: "", email: "", firm_name: "", primary_need: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please email us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 md:py-36 relative z-10" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="max-w-3xl">
          <span className="font-sans text-xs uppercase tracking-[0.28em] text-verdigris">
            Contact
          </span>
          <FadeUp>
            <h2 className="font-serif text-petrol text-4xl md:text-6xl tracking-tight mt-8 leading-[1.02]">
              Ready to scale your practice?
            </h2>
            <p className="font-sans text-petrol/70 text-lg mt-6 leading-relaxed max-w-xl">
              Fill out your details below or email us directly to arrange an
              initial conversation.
            </p>
          </FadeUp>
        </div>

        <div className="grid grid-cols-1 mt-16 md:mt-20 max-w-3xl">
          {/* Lead form (Resend notification) */}
          <div>
            <FadeUp>
              <form
                onSubmit={handleSubmit}
                className="bg-parchment-dark border border-petrol/20 p-8 md:p-10 space-y-8"
                data-testid="inquiry-form"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div>
                    <label className="font-sans text-xs uppercase tracking-[0.16em] text-petrol/60">
                      Full Name *
                    </label>
                    <input
                      data-testid="input-full-name"
                      value={form.full_name}
                      onChange={(e) => update("full_name", e.target.value)}
                      className={inputClass}
                      placeholder="Jane Advisor"
                    />
                  </div>
                  <div>
                    <label className="font-sans text-xs uppercase tracking-[0.16em] text-petrol/60">
                      Email Address *
                    </label>
                    <input
                      data-testid="input-email"
                      type="email"
                      value={form.email}
                      onChange={(e) => update("email", e.target.value)}
                      className={inputClass}
                      placeholder="jane@firm.co.uk"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div>
                    <label className="font-sans text-xs uppercase tracking-[0.16em] text-petrol/60">
                      Firm Name *
                    </label>
                    <input
                      data-testid="input-firm-name"
                      value={form.firm_name}
                      onChange={(e) => update("firm_name", e.target.value)}
                      className={inputClass}
                      placeholder="Your practice"
                    />
                  </div>
                  <div>
                    <label className="font-sans text-xs uppercase tracking-[0.16em] text-petrol/60 block mb-1">
                      Primary Need *
                    </label>
                    <Select value={form.primary_need} onValueChange={(v) => update("primary_need", v)}>
                      <SelectTrigger
                        data-testid="select-primary-need"
                        className="w-full bg-transparent border-0 border-b border-petrol/25 rounded-none py-3.5 h-auto px-0 font-sans text-petrol focus:ring-0 focus:border-verdigris"
                      >
                        <SelectValue placeholder="Select an option" />
                      </SelectTrigger>
                      <SelectContent className="bg-parchment border-petrol/20 rounded-none">
                        {NEEDS.map((n) => (
                          <SelectItem key={n.value} value={n.value} data-testid={`need-option-${n.value.replace(/\s+/g, "-").toLowerCase()}`}>
                            {n.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <label className="font-sans text-xs uppercase tracking-[0.16em] text-petrol/60">
                    Message / Notes
                  </label>
                  <textarea
                    data-testid="input-message"
                    value={form.message}
                    onChange={(e) => update("message", e.target.value)}
                    rows={4}
                    className={`${inputClass} resize-none`}
                    placeholder="Tell us about your current capacity challenges…"
                  />
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="submit-inquiry-btn"
                  className="group w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-petrol text-parchment px-10 py-4 font-sans text-sm uppercase tracking-[0.16em] hover:bg-petrol/90 transition-colors disabled:opacity-60"
                >
                  {submitting ? <Loader2 size={18} className="animate-spin" /> : null}
                  {submitting ? "Sending…" : "Send Inquiry"}
                  {!submitting && <ArrowUpRight size={18} className="group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform" />}
                </button>
                <a
                  href={`mailto:${EMAIL}?subject=Inquiry%20from%20Website`}
                  data-testid="email-us-directly-btn"
                  className="group inline-flex items-center justify-center gap-2 sm:ml-4 border border-petrol text-petrol px-8 py-4 font-sans text-sm uppercase tracking-[0.16em] hover:bg-petrol hover:text-parchment transition-colors w-full sm:w-auto mt-4 sm:mt-0"
                >
                  Email Us Directly
                  <ArrowUpRight size={18} className="group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform" />
                </a>
              </form>
            </FadeUp>
          </div>
        </div>
      </div>
    </section>
  );
};
